// signierlink.ts — „Unterschreiben lassen" ohne Browser-Dialog (User 28.09.2026):
//
//  - Gibt es für die Vollmacht schon einen gültigen Link (nicht abgelaufen,
//    nicht widerrufen — widerrufene liefert der Server gar nicht erst mit),
//    öffnet der Klick ihn direkt in einem neuen Fenster.
//  - Sonst wird ein neuer Link erzeugt und im eigenen Popup gezeigt
//    (`SignierlinkDialog`).
//
// Popup-Blocker (Edge!): `window.open` muss im Klick-Handler laufen, bevor das
// erste `await` kommt. `unterschreibenLassen` öffnet deshalb SYNCHRON, noch vor
// jedem `await` — der Aufrufer muss es direkt aus dem Klick heraus aufrufen.

import type { VollmachtDto } from "./vollmachtTypes";

/** Was das Popup zu einem Link zeigt. */
export type SignierlinkAnzeige = { titel: string; url: string; expiresAt: string };

type Link = NonNullable<VollmachtDto["link"]>;

/** Der noch nutzbare Link der Vollmacht — oder `null`. */
export function gueltigerLink(v: VollmachtDto | null | undefined, jetzt: number = Date.now()): Link | null {
  const link = v?.link;
  if (!link || link.abgelaufen || !link.url) return null;
  const ablauf = new Date(link.expiresAt).getTime();
  if (Number.isFinite(ablauf) && ablauf < jetzt) return null;
  return link;
}

const liegtVor = (v?: VollmachtDto | null): boolean => v?.status === "hochgeladen" || v?.status === "unterschrieben";

/**
 * Knopf „Unterschreiben lassen" gesperrt? Ein vorhandener gültiger Link ist
 * immer zu öffnen; einen neuen gibt es nur mit Schreibrecht und solange die
 * Vollmacht nicht schon vorliegt.
 */
export function unterschreibenGesperrt(v: VollmachtDto | null | undefined, darfAendern: boolean): boolean {
  if (gueltigerLink(v)) return false;
  return !darfAendern || liegtVor(v);
}

/** Link in einem neuen Fenster/Tab, ohne Rückgriff der Seite auf KMVS. */
export function linkInNeuemFenster(url: string): void {
  window.open(url, "_blank", "noopener,noreferrer");
}

/**
 * Vorhandenen Link öffnen (→ `null`) oder neuen erzeugen (→ Anzeige fürs Popup).
 * Direkt aus dem Klick aufrufen — geöffnet wird vor dem ersten `await`.
 */
export async function unterschreibenLassen(opts: {
  vollmacht: VollmachtDto | null | undefined;
  titel: string;
  erzeugen: () => Promise<{ url: string; expiresAt: string | Date }>;
  oeffnen?: (url: string) => void;
}): Promise<SignierlinkAnzeige | null> {
  const vorhanden = gueltigerLink(opts.vollmacht);
  if (vorhanden) {
    (opts.oeffnen ?? linkInNeuemFenster)(vorhanden.url);
    return null;
  }
  const { url, expiresAt } = await opts.erzeugen();
  return { titel: opts.titel, url, expiresAt: expiresAt instanceof Date ? expiresAt.toISOString() : String(expiresAt ?? "") };
}

/** „gültig bis 12.10.2026" — leer bei unbekanntem Datum. */
export function gueltigBisText(expiresAt: string): string {
  const d = new Date(expiresAt);
  if (!expiresAt || Number.isNaN(d.getTime())) return "";
  return `gültig bis ${d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" })}`;
}
